/**
 * Verify Page Component
 *
 * Magic link verification page that handles token validation.
 * Automatically extracts token from URL and verifies with the API.
 *
 * Features:
 * - Automatic token extraction from URL query parameter
 * - Auto-verification on page load
 * - Loading, success, and error states
 * - Redirect to stored destination after successful verification
 * - i18n support for all user-facing text
 * - Graceful handling of missing/invalid tokens
 *
 * @module features/auth/pages/VerifyPage
 * @see SPEC-005.5-auth-login-ui Requirements 2.1-2.8
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTypedTranslation } from '../../../i18n';
import { useAuthStore } from '../store/authStore';
import { authApiClient } from '../api/authApiClient';
import type { ApiError } from '@linkode/shared';
import { Card, Button } from '../../../stories/landing';
import '../../../stories/landing/landing.css';

// ============================================================================
// Constants
// ============================================================================

/** Key for storing redirect destination in sessionStorage */
const REDIRECT_STORAGE_KEY = 'linkode_auth_redirect';

/** Default redirect destination after successful verification */
const DEFAULT_REDIRECT = '/dashboard';

/** Delay before redirecting after successful verification (ms) */
const REDIRECT_DELAY = 1000;

// ============================================================================
// Types
// ============================================================================

/**
 * Verify page state machine states
 * - loading: Verification in progress
 * - success: Token verified successfully
 * - error: Verification failed
 */
type VerifyState = 'loading' | 'success' | 'error';

// ============================================================================
// Component
// ============================================================================

/**
 * Verify Page Component
 *
 * Handles magic link verification flow:
 * 1. Extracts token from URL query parameter
 * 2. Calls verify API automatically
 * 3. On success: stores session and redirects
 * 4. On error: shows error message with link to request new magic link
 *
 * @example
 * ```tsx
 * // In router configuration
 * <Route path="/auth/verify" element={<VerifyPage />} />
 *
 * // User navigates to: /auth/verify?token=abc123
 * ```
 *
 * @see Requirements 2.1-2.8
 */
export function VerifyPage() {
  const { t } = useTypedTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auth store
  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Component state
  const [state, setState] = useState<VerifyState>('loading');
  const [_error, setError] = useState<ApiError | null>(null);

  // Ref to prevent double verification in StrictMode
  const verificationStarted = useRef(false);

  // Extract token from URL
  // @see Requirements 2.1
  const token = searchParams.get('token');

  // Handle already authenticated users
  // @see Requirements 2.4, 2.5
  useEffect(() => {
    if (isAuthenticated) {
      const storedDestination = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
      sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
      navigate(storedDestination || DEFAULT_REDIRECT, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Handle missing token
  // @see Requirements 2.7
  useEffect(() => {
    if (!token && !isAuthenticated) {
      navigate('/login', {
        replace: true,
        state: { error: 'auth.error.missingToken' },
      });
    }
  }, [token, isAuthenticated, navigate]);

  // Verify token on mount
  // @see Requirements 2.2, 2.3
  useEffect(() => {
    // Skip if no token, already authenticated, or verification already started
    if (!token || isAuthenticated || verificationStarted.current) {
      return;
    }

    // Mark verification as started (prevent double-call in StrictMode)
    verificationStarted.current = true;

    const verifyToken = async () => {
      try {
        // Call verify API
        // @see Requirements 2.2
        const { sessionToken, expiresAt, userId, email } = await authApiClient.verifyToken(token);

        // Login and store session
        // @see Requirements 2.4
        login(sessionToken, expiresAt, userId, email);
        setState('success');

        // Redirect after short delay for UX
        // @see Requirements 2.4, 2.5
        setTimeout(() => {
          const storedDestination = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
          sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
          navigate(storedDestination || DEFAULT_REDIRECT, { replace: true });
        }, REDIRECT_DELAY);
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError);
        setState('error');

        // Log error in development (without sensitive data)
        // @see Requirements 10.2 - Token security in logs
        if (import.meta.env.DEV) {
          console.error('[Auth] Token verification failed:', {
            code: apiError.code,
            requestId: apiError.requestId,
            // Note: Do NOT log the token itself
          });
        }
      }
    };

    verifyToken();
  }, [token, isAuthenticated, login, navigate]);

  // Don't render anything if redirecting due to missing token
  if (!token && !isAuthenticated) {
    return null;
  }

  const handleRetry = () => navigate('/login');

  return (
    <div
      className="verify-page landing-base"
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-background)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div className="verify-container" style={{ width: '100%', maxWidth: '420px' }}>
        {/* Main Card */}
        <Card hoverable={false}>
          {/* Loading State */}
          {/* @see Requirements 2.3 */}
          {state === 'loading' && (
            <div className="verify-loading" style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '16px' }}>
                {/* Spinner */}
                <div
                  style={{
                    margin: '0 auto',
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    style={{
                      width: '40px',
                      height: '40px',
                      color: 'var(--color-primary)',
                      animation: 'spin 1s linear infinite',
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      style={{ opacity: 0.25 }}
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      style={{ opacity: 0.75 }}
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              </div>
              <p
                className="font-mono"
                style={{
                  fontSize: '18px',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                }}
                role="status"
              >
                {t('auth.verify.verifying')}
              </p>
            </div>
          )}

          {/* Success State */}
          {/* @see Requirements 2.4 */}
          {state === 'success' && (
            <div className="verify-success" style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    margin: '0 auto',
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'var(--color-accent-teal)',
                    border: '2px solid var(--color-border-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    style={{ width: '24px', height: '24px', color: 'var(--color-white)' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p
                className="font-mono"
                style={{
                  fontSize: '18px',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                  marginBottom: '8px',
                }}
                role="status"
              >
                {t('auth.verify.success')}
              </p>
              <p
                className="verify-redirect"
                style={{
                  fontSize: '14px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {t('auth.verify.redirecting')}
              </p>
            </div>
          )}

          {/* Error State */}
          {/* @see Requirements 2.6 */}
          {state === 'error' && (
            <div className="verify-error" style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    margin: '0 auto',
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'var(--color-accent-red)',
                    border: '2px solid var(--color-border-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    style={{ width: '24px', height: '24px', color: 'var(--color-white)' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>
              <h2
                className="font-mono"
                style={{
                  fontSize: '18px',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                  marginBottom: '8px',
                }}
              >
                {t('auth.verify.errorTitle')}
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '24px',
                }}
                role="alert"
              >
                {t('auth.verify.errorMessage')}
              </p>
              {/* Button to request new magic link */}
              {/* @see Requirements 2.6 */}
              <Button variant="primary" onClick={handleRetry} fullWidth>
                {t('auth.verify.requestNewLink')}
              </Button>
            </div>
          )}
        </Card>
      </div>
      {/* eslint-disable-next-line react/jsx-no-literals -- CSS keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default VerifyPage;
